import React, { useState } from 'react';
import {
  IAuditoriaAdiantamentoSalarioResultado,
  IBaixaAdiantamentoSalario,
  IItemAdiantamentoSalario,
} from '../interfaces/IAuditoriaAdiantamentoSalario';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaAdiantamentoSalarioResultado;
}

// ─── Painel de memória de conciliação ────────────────────────────────────────

const estiloLinha: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '3px 0', borderBottom: '1px solid #e5e7eb', fontSize: 12,
};
const estiloRotulo: React.CSSProperties = { color: '#6b7280', minWidth: 120 };
const estiloValor: React.CSSProperties = { fontWeight: 500, textAlign: 'right' };
const estiloContaTag: React.CSSProperties = {
  display: 'inline-block', background: '#f3f4f6', color: '#374151',
  fontSize: 11, padding: '1px 6px', borderRadius: 3, fontFamily: 'monospace',
};

const LinhaMemoria: React.FC<{ rotulo: string; valor: React.ReactNode }> = ({ rotulo, valor }) => (
  <div style={estiloLinha}>
    <span style={estiloRotulo}>{rotulo}</span>
    <span style={estiloValor}>{valor}</span>
  </div>
);

const MemoriaConciliacao: React.FC<{ item: IItemAdiantamentoSalario }> = ({ item }) => {
  const temBaixa = item.motivoConciliacao === 'Baixa na ECD' && item.baixasUtilizadas?.length > 0;
  const corFundo = item.status === 'AVISO' ? '#fffbeb' : '#f0fdf4';
  const corBorda = item.status === 'AVISO' ? '#fcd34d' : '#86efac';

  return (
    <div style={{
      background: corFundo, border: `1px solid ${corBorda}`, borderRadius: 6,
      padding: '10px 14px', marginTop: 8, fontSize: 12,
    }}>
      {/* Bloco: lançamento original do adiantamento */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontWeight: 600, marginBottom: 4, color: '#1e3a5f', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Adiantamento Original
        </div>
        <LinhaMemoria rotulo="Data" valor={formatDate(item.dataLancamento)} />
        <LinhaMemoria rotulo="Valor" valor={<strong>{formatCurrency(item.valorBase)}</strong>} />
        {item.historico && <LinhaMemoria rotulo="Histórico" valor={item.historico} />}
        <LinhaMemoria
          rotulo="D (debitada)"
          valor={<span style={estiloContaTag}>{item.contaContabil}{item.descricaoConta ? ` — ${item.descricaoConta}` : ''}</span>}
        />
        {item.contaContraPartida && (
          <LinhaMemoria
            rotulo="C (creditada)"
            valor={<span style={estiloContaTag}>{item.contaContraPartida}</span>}
          />
        )}
      </div>

      {/* Bloco: baixas encontradas na ECD */}
      {temBaixa && (
        <div style={{ marginBottom: 8, borderTop: `1px dashed ${corBorda}`, paddingTop: 8 }}>
          <div style={{ fontWeight: 600, marginBottom: 4, color: '#166534', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Baixa encontrada na ECD
          </div>
          {item.baixasUtilizadas.map((b: IBaixaAdiantamentoSalario, i: number) => (
            <div key={i} style={{ marginBottom: 6 }}>
              <LinhaMemoria
                rotulo={b.ehArredondamento ? 'Data (arredond.)' : 'Data'}
                valor={
                  <span>
                    {formatDate(b.data)}
                    {b.ehArredondamento && (
                      <span style={{ marginLeft: 6, background: '#fef3c7', color: '#92400e', fontSize: 10, padding: '1px 5px', borderRadius: 3 }}>
                        arredondamento
                      </span>
                    )}
                  </span>
                }
              />
              <LinhaMemoria rotulo="Valor" valor={<span style={{ color: '#166534' }}>{formatCurrency(b.valor)}</span>} />
              {b.historico && <LinhaMemoria rotulo="Histórico" valor={b.historico} />}
              {b.contaContraPartida && (
                <LinhaMemoria
                  rotulo="D (debitada)"
                  valor={<span style={estiloContaTag}>{b.contaContraPartida}</span>}
                />
              )}
              <LinhaMemoria
                rotulo="C (creditada)"
                valor={<span style={estiloContaTag}>{b.conta}{item.descricaoConta ? ` — ${item.descricaoConta}` : ''}</span>}
              />
            </div>
          ))}
        </div>
      )}

      {/* Bloco: totais e status */}
      <div style={{ borderTop: `1px dashed ${corBorda}`, paddingTop: 8 }}>
        <div style={{ fontWeight: 600, marginBottom: 4, color: '#1e3a5f', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Resultado
        </div>
        {temBaixa && (
          <>
            <LinhaMemoria rotulo="Total baixado" valor={<strong style={{ color: '#166534' }}>{formatCurrency(item.totalBaixado)}</strong>} />
            {item.diferencaArredondamento > 0 && (
              <LinhaMemoria
                rotulo="Diferença"
                valor={<span style={{ color: '#92400e' }}>{formatCurrency(item.diferencaArredondamento)} (arredondamento)</span>}
              />
            )}
          </>
        )}
        <LinhaMemoria rotulo="Dias em aberto" valor={`${item.diasSemBaixa} dia(s)`} />
        <LinhaMemoria
          rotulo="Status"
          valor={
            <span style={{
              background: item.status === 'AVISO' ? '#fef3c7' : '#dcfce7',
              color: item.status === 'AVISO' ? '#92400e' : '#166534',
              padding: '2px 8px', borderRadius: 4, fontWeight: 600,
            }}>
              {item.status === 'CONCILIADO' && item.motivoConciliacao
                ? `${item.status} — ${item.motivoConciliacao}`
                : item.status}
            </span>
          }
        />
        {item.memoriaCalculo && (
          <div style={{ marginTop: 6, color: '#6b7280', fontSize: 11 }}>
            <strong>Base de cálculo:</strong> {item.memoriaCalculo}
          </div>
        )}
      </div>
    </div>
  );
};

const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

const AuditoriaAdiantamentoSalarioDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const adiantamentosPendentes = resultado.adiantamentosPendentes || [];
  const adiantamentosConciliados = resultado.adiantamentosConciliados || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Adiantamento de Salários — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Multa ECD potencial — 5% sobre o valor pendente, limitada a 1% da receita bruta do período (Art. 12, II, Lei 8.218/1991).</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>Adiantamentos salariais pendentes de conciliação por mais de 30 dias no período.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base fiscal e contábil aplicada a esta auditoria.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Fundamentação Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
          <div className='mt-2'><strong>Fundamentação Contábil:</strong> {resultado.fundamentacaoContabil}</div>
        </div>
      )}

      <div className='auditoria-card'>
        <h3>Adiantamentos Salariais Pendentes (&gt; 30 dias sem baixa/folha)</h3>
        <p className='text-muted'>
          Lançamentos de adiantamento salarial/a pessoal cujo crédito de baixa/compensação na folha <strong>não foi
          encontrado na ECD</strong> em até 30 dias — indício de despesa de remuneração não reconhecida no período
          de competência devido. Adiantamentos com baixa dentro do prazo aparecem como Conciliados abaixo.
        </p>

        {adiantamentosPendentes.length === 0 && (
          <div className='text-muted mt-4'>Nenhum adiantamento salarial sem baixa identificada na ECD no período.</div>
        )}

        {adiantamentosPendentes.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Data Lançamento</th>
                  <th className='text-center'>Dias s/ Baixa</th>
                  <th className='text-end'>Valor Base</th>
                  <th className='text-end'>Multa ECD Potencial</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {adiantamentosPendentes.map((item: IItemAdiantamentoSalario, idx) => {
                  const chave = `as-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{formatDate(item.dataLancamento)}</td>
                        <td className='text-center'>
                          <span className={`auditoria-status status-${item.status}`}>{item.diasSemBaixa} dias</span>
                        </td>
                        <td className='text-end'>{formatCurrency(item.valorBase)}</td>
                        <td className='text-end'>{formatCurrency(item.impactoFiscalTotal)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={6} style={{ background: '#fffbeb' }}>
                            <div className='py-2 px-3'>
                              <div style={{ fontSize: 12, color: '#92400e', marginBottom: 4 }}>
                                <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                              </div>
                              {item.statusConciliacao && (
                                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 4 }}>
                                  <strong>Motor de conciliação:</strong> {item.statusConciliacao}
                                </div>
                              )}
                              <MemoriaConciliacao item={item} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {adiantamentosConciliados.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
            Adiantamentos Salariais Verificados e Conciliados
          </h3>
          <p className='text-muted'>
            Lançamentos de adiantamento salarial com baixa/compensação identificada na ECD dentro do prazo de 30 dias,
            ou já conciliados pelo motor de conciliação do sistema.
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Data Adiantamento</th>
                  <th>Data Baixa</th>
                  <th className='text-center'>Dias p/ Baixa</th>
                  <th className='text-end'>Valor</th>
                  <th>Critério</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {adiantamentosConciliados.map((item: IItemAdiantamentoSalario, idx) => {
                  const chave = `asc-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;
                  const isBaixaEcd = item.motivoConciliacao === 'Baixa na ECD';

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{formatDate(item.dataLancamento)}</td>
                        <td>
                          {item.dataBaixa
                            ? <span style={{ color: '#166534', fontWeight: 500 }}>{formatDate(item.dataBaixa)}</span>
                            : <span className='text-muted'>—</span>}
                        </td>
                        <td className='text-center'>{item.diasSemBaixa}</td>
                        <td className='text-end'>{formatCurrency(item.valorBase)}</td>
                        <td>
                          {item.motivoConciliacao && (
                            <span style={{
                              background: isBaixaEcd ? '#dcfce7' : '#e0f2fe',
                              color: isBaixaEcd ? '#166534' : '#0369a1',
                              fontSize: 11, fontWeight: 500, padding: '2px 7px', borderRadius: 4
                            }}>
                              {item.motivoConciliacao}
                            </span>
                          )}
                        </td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={7} style={{ background: '#f0fdf4' }}>
                            <div className='py-2 px-3'>
                              <div style={{ fontSize: 12, color: '#166534', marginBottom: 4 }}>
                                <i className='bi bi-check-circle-fill me-1' />
                                <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                              </div>
                              <MemoriaConciliacao item={item} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaAdiantamentoSalarioDetalhe;
